<template>
    <div v-if="!showLoader" class="cashflow-tab-content-wrapper" :class="[variant]">
        <div class="top-content-wrapper">
            <div class="tabs-and-title-wrapper">
                <div class="title">{{t('home.widgets.cashflow.currency')}}</div>
                <tab-segment tabSize="small" :tabsList="currencyTabsList" @clicked="updateFiltersHandler('currency', $event)"/>
            </div>
            <div class="date-filters-wrapper">
                <!-- Init Date -->
                <input-calendar
                :params="{ labelText: t('home.widgets.cashflow.initDate')}"
                :inputValue="appliedFilters.initDate"
                @input="updateFiltersHandler('initDate', $event)"/>
                <!-- End Date -->
                <input-calendar
                :params="{ labelText: t('home.widgets.cashflow.endDate')}"
                :inputValue="appliedFilters.endDate"
                @input="updateFiltersHandler('endDate', $event)"/>
            </div>
        </div>
        <div class="bottom-content-wrapper">
        </div>
    </div>
    <!-- Loader Generic -->
    <loaders-generic v-else fullSize/>
</template>

<script setup>
import TabSegment from '@/components/generic/tabs/Segment'
import InputCalendar from '@/components/generic/inputs/Calendar'
import LoadersGeneric from '@/components/generic/loaders/Generic'
/* Vue & Assets */
import { ref, computed, watch } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['tabClicked', 'redirectToFeature', 'applyCashflowErrorState'])

/* Props
@showPatrimonyLoader {Boolean} - prop to tell widget patrimony fetch state
@variant {String} - can be [ none (default), anchor]
*/
const props = defineProps({
    showPatrimonyLoader: Boolean,
    variant: String
})

/* mapActions & mapGetters */
const { requestCashflowInfo } = mapActions(moduleMapper.HOME)
const { getCashflowInfo, getPatrimonyInfo, getHomeAccounts } = mapGetters(moduleMapper.HOME)

/* Data */
const showLoader = ref(props.showPatrimonyLoader)

const currencyTabsList = computed(() => {
    return getPatrimonyInfo.value?.currenciesFromAccounts.map(currency => {
        return {
            id: currency.name,
            label: currency.name === 'AKZ' ? 'Kz' : currency.name,
            selected: appliedFilters.value.name === currency.name
        }
    })
})

/*************************************************
*                                                *
*               Request Cashflow Info            *
*                                                *
*************************************************/
watch(() => props.showPatrimonyLoader, (newVal, oldVal) => {
    if (!props.showPatrimonyLoader && getHomeAccounts.value.length) fetchCashflowInfo()
})

const INITIAL_CASHFLOW_FILTERS = {
    currency: 'AKZ',
    initDate: new Date(),
    endDate: new Date()
}

const appliedFilters = ref(INITIAL_CASHFLOW_FILTERS)

const updateFiltersHandler = (field, event) => {
    appliedFilters.value[field] = event
}

const fetchCashflowInfo = async () => {
    showLoader.value = true
	emit('applyCashflowErrorState', false)

    const payload = {
        accountId: getHomeAccounts.value.map(obj => {
            return obj.id
        })
    }

	try {
        await requestCashflowInfo(payload)
        showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
        emit('applyCashflowErrorState', true)
	}
}
</script>

<style lang="scss" scoped>
.cashflow-tab-content-wrapper {
    width: 100%;
    padding: 16px 24px 0 24px;

    display: flex;
    flex-direction: column;

    .top-content-wrapper {
        width: 100%;
        margin-bottom: 36px;

        display: flex;
        justify-content: space-between;
        gap: 4px;

        .tabs-and-title-wrapper {
            width: 100%;
            max-width: 316px;
            height: auto;

            .title {
                width: 100%;
                height: 16px;
                margin-bottom: 8px;

                @include H6-Regular;
                color: var(--secondary-dark-2);
            }

            :deep(.tabs-segment-wrapper) {
                width: fit-content;

                .tab {
                    width: 62px;
                }
            }
        }

        .date-filters-wrapper {
            display: flex;
            flex-shrink: 0;
            gap: 4px;

            .input-elements-container {
                width: 112px;
            }
        }
    }

    .bottom-content-wrapper {
        width: 100%;
    }

    &.anchor {
        .top-content-wrapper {
            .tabs-and-title-wrapper {
                :deep(.tabs-segment-wrapper) {
                    .tab {
                        width: 100%;
                    }
                }
            }
        }
    }
}
</style>
